import { createConsumer } from "@rails/actioncable";

// ActionCable consumer for real-time notifications.
//
// The WebSocket handshake is a plain GET that can't carry
// devise-token-auth's custom headers, so we pass the same triplet
// (`access-token` / `client` / `uid`) as query params; the BE
// validates them in ApplicationCable::Connection.
//
// We connect to `/api/cable` (origin-relative) so the same path
// works in every environment: Vite proxies `/api` → backend in dev
// (with `ws: true`), and nginx proxies it on QA/prod. The scheme is
// derived from the page (https → wss).

const buildCableUrl = () => {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const qs = new URLSearchParams({
    uid: localStorage.getItem("uid") || "",
    client: localStorage.getItem("client") || "",
    "access-token": localStorage.getItem("access-token") || "",
  }).toString();
  return `${proto}//${window.location.host}/api/cable?${qs}`;
};

let consumer = null;
let consumerToken = null;

const getConsumer = () => {
  const token = localStorage.getItem("access-token") || "";

  // Rebuild only when the token actually changed (a re-login), so the new
  // auth params are used. This used to disconnect on every call, which was
  // harmless while notifications were the only channel — CREW-1205 puts a
  // second one on the same consumer, and rebuilding for the second would
  // pull the socket out from under the first.
  if (!consumer || consumerToken !== token) {
    if (consumer) consumer.disconnect();
    consumer = createConsumer(buildCableUrl());
    consumerToken = token;
  }

  return consumer;
};

// `channel` is the channel name, or `{ channel, ...params }` for a channel
// that needs parameters (DocumentImportsChannel takes the import id).
const subscribe = (channel, { onReceived, onConnected, onDisconnected }) => {
  const subscription = getConsumer().subscriptions.create(channel, {
    connected: onConnected,
    disconnected: onDisconnected,
    received: onReceived,
  });

  return () => {
    try {
      subscription.unsubscribe();
    } catch (e) {
      // ignore — consumer may already be torn down
    }
  };
};

/**
 * Subscribe to the NotificationChannel.
 * @param {Object} handlers
 * @param {(data:Object)=>void} handlers.onState   - received payload
 * @param {()=>void} handlers.onConnected
 * @param {()=>void} handlers.onDisconnected
 * @returns {()=>void} unsubscribe
 */
export const subscribeNotifications = ({ onState, onConnected, onDisconnected }) =>
  subscribe("NotificationChannel", { onReceived: onState, onConnected, onDisconnected });

/**
 * Subscribe to the VacanciesChannel (CREW-1205).
 *
 * One shared stream carries every vacancy — the list is a surface several
 * recruiters work at once — so the caller decides whether a given row is on
 * screen. The BE rejects the subscription outright without read access to
 * vacancies, in which case no payload ever arrives.
 *
 * @param {Object} handlers
 * @param {(data:Object)=>void} handlers.onVacancy - `{ id, state, assignees, candidates }`
 * @param {()=>void} [handlers.onConnected]
 * @param {()=>void} [handlers.onDisconnected]
 * @returns {()=>void} unsubscribe
 */
export const subscribeVacancies = ({ onVacancy, onConnected, onDisconnected }) =>
  subscribe("VacanciesChannel", { onReceived: onVacancy, onConnected, onDisconnected });

/**
 * Subscribe to one DocumentImportsChannel stream (CREW-1277).
 *
 * The backend pushes `{ type: 'file', ...file, import_status }` as each file
 * settles, and transmits `{ type: 'import', ...import }` once on subscribe —
 * which is also how a tab that reconnected catches up. The subscription is
 * rejected for anybody but the import's author, in which case nothing arrives.
 *
 * @param {string|number} importId
 * @param {Object} handlers
 * @param {(data:Object)=>void} handlers.onMessage
 * @param {()=>void} [handlers.onConnected]
 * @param {()=>void} [handlers.onDisconnected]
 * @returns {()=>void} unsubscribe
 */
export const subscribeDocumentImport = (importId, { onMessage, onConnected, onDisconnected }) =>
  subscribe(
    { channel: "DocumentImportsChannel", import_id: importId },
    { onReceived: onMessage, onConnected, onDisconnected },
  );
